// Per-tenant local-data wipe helpers used by the logout modal
// (SidebarFooter → LogoutDialog) and the on-demand controls in
// SettingsPage's "Clear local data" section. Addresses Cobalt finding
// PT38031_6: residual sensitive data in browser storage after sign-out.
//
// Two independently invokable buckets - chat history and workflows -
// so the user can choose what to keep. Each helper is
// idempotent and fail-soft; a thrown error from one storage layer
// never blocks the others.
//
// Tenant scoping is non-negotiable: a browser that has signed into
// tenant A and tenant B must lose only the active tenant's data on
// any single wipe. The composite-key chat/workflow stores make that
// natural.
//
// What's deliberately NOT wiped:
//   - Auth tokens - those clear unconditionally in authBridge.logout().
//   - Pure UI prefs spanning the install (theme, dashboard:columns:*) -
//     not sensitive, span the install not the session.
//   - Other tenants' chat / workflow rows.

import { deleteAllChatsForTenant } from "./chatStorage.js";
import { clearStarterCardsForTenant } from "./starterCardsStore.js";
import { clearStoredTraceNotes } from "./traceNoteStore.js";
import { clearStoredInvestigations } from "./investigateStore.js";
import { clearKeptRegisterReading } from "./registerReadingStore.js";
import { deleteAllWorkflowsForTenant } from "./workflowStore.js";
import { getCurrentTenantIdSync } from "./userInfo.js";
import { browserStorageKey } from "./browserStorageScope.js";

// Shared by every destructive UI that invokes clearChatData. Keeping the copy
// beside the implementation makes Settings state the same
// preservation boundary this wipe enforces below.
export const CHAT_DATA_CLEAR_COPY = Object.freeze({
    description:
        "Conversations, prompts, and control proposal drafts saved in this browser. Local kernels, canvases, pins, and pending cloud-recovery data are preserved.",
    confirmation:
        "Conversations, control proposal drafts, and chat-adjacent state in this browser will be deleted. Local kernels, canvases, pins, and pending cloud-recovery data are preserved. Server-side data is unaffected.",
});

// Resolve the active tenant ID. Prefer the explicit argument (lets
// callers target a specific tenant), then fall back to the cached
// /v1/me result. authBridge's `tenant_id` localStorage key is NOT a
// reliable source here: the current dashboard never writes it (the
// stale comment in authBridge.js predates the dashboard rewrite), so
// reads of it return null in production and silently retarget every
// wipe at the synthetic "default" tenant key. The "default" fallback
// is kept only for the truly-no-tenant case (which would only happen
// before /v1/me resolves; in that state there's nothing tenant-scoped
// to clear anyway).
function tk(tenantId) {
    const resolved = tenantId ?? getCurrentTenantIdSync();
    return String(resolved || "default");
}

function safeRemove(store, key) {
    if (!key) return;
    try { store.removeItem(key); } catch { /* storage unavailable - ignore */ }
}

function safeRemoveScoped(prefix, tenantId) {
    safeRemove(localStorage, browserStorageKey(prefix, tenantId));
}

// Chat history bucket: the origin-chat IndexedDB rows for this tenant
// plus localStorage keys that hold chat-adjacent state. Legacy kernels,
// canvases, pins, and durable import/create recovery records are
// deliberately excluded: the one-time migration drains successfully imported
// records individually, so any record still present may be the user's only
// recoverable copy.
export async function clearChatData(tenantId) {
    // Race shape: ChatConsole holds the active transcript in React
    // state and has window.beforeunload / visibilitychange handlers
    // that call saveChat. Without coordination, the next navigation
    // (or even a debounce tick) re-persists the in-memory transcript
    // right back into IDB after our delete. deleteAllChatsForTenant
    // fires subscribeChatsCleared on completion; ChatConsole's
    // subscriber resets its chats array, so any subsequent
    // flushPendingSaves sees empty state and has nothing to save.
    // Callers logging the user out should additionally call
    // chatStorage.suppressFurtherWrites() before this to belt-and-
    // suspenders the redirect-to-WorkOS window.
    const t = tk(tenantId);
    try {
        const { clearWorkspacePolicies } = await import("./workspacePolicy.js");
        await clearWorkspacePolicies(t);
    } catch (err) { console.warn("[clearUserData] clearWorkspacePolicies threw:", err); }
    try { await deleteAllChatsForTenant(t); } catch (err) {
        console.warn("[clearUserData] deleteAllChatsForTenant threw:", err);
    }
    safeRemove(localStorage, `origin-chat-active-id:${t}`);
    // The notes chats pinned on traces: what an answer pointed at, often in
    // its own words about the trace.
    try { clearStoredTraceNotes(t); } catch (err) {
        console.warn("[clearUserData] clearStoredTraceNotes threw:", err);
    }
    // What Investigate made of each scope: cases, the notes its digs wrote in
    // the sessions' own words, its checks, and which chats it opened on them.
    try { clearStoredInvestigations(t); } catch (err) {
        console.warn("[clearUserData] clearStoredInvestigations threw:", err);
    }
    safeRemoveScoped("origin-investigate-chats-v1", t);
    // Signals localStorage. Signals now ship native + read-only (no client-side
    // authoring/seeding), so these keys are written by nothing and read by
    // nothing — reap them here so a browser that ran the old dark feature
    // doesn't keep stranded blobs (the seeded triggers + their marker, the
    // deleted fixtures store, and the pre-tenant-scoping `:default` buckets that
    // the removed purgeLegacyTriggers used to reclaim). O-2302.
    safeRemove(localStorage, `originChat.triggers.v1:${t}`);
    safeRemove(localStorage, `originChat.triggers.seeded.v1:${t}`);
    safeRemove(localStorage, `originChat.triggerFixtures.v1:${t}`);
    safeRemove(localStorage, `originChat.triggerFixtures.v1`);
    safeRemove(localStorage, `originChat.triggers.v1:default`);
    safeRemove(localStorage, `originChat.triggers.seeded.v1:default`);
    safeRemove(localStorage, `originChat.userPrompts.v1:${t}`);
    safeRemoveScoped("originChat.userPrompts.v2", t);
    // Both constitutions: the supplied policy text, and the document the
    // discovery agent mined from this tenant's own traces (its inferred
    // objectives and observed practice). The discovered one is a profile OF
    // the customer, so leaving it behind is the worse half to forget.
    safeRemove(localStorage, `origin.constitution.v1:${t}`);
    safeRemove(localStorage, `origin.constitution.discovered.v1:${t}`);
    safeRemoveScoped("origin.constitution.v2", t);
    safeRemoveScoped("origin.constitution.discovered.v2", t);
    safeRemove(localStorage, `tenant:${t}:externalLlmTaskModel`);
    safeRemoveScoped("origin.chat.taskModel.v2", t);
    // Tenant-tailored starter cards — the profiling-run output, which
    // embeds the org profile + endpoint-specific question text. Wiped via
    // the store so the in-memory snapshot resets too when it's the active
    // tenant (not just the localStorage key).
    clearStarterCardsForTenant(t);
    // A register reading kept from a file: the organization's groups, people
    // and norms. Like the discovered constitution it is a profile of the
    // tenant, so it goes with this bucket.
    try { await clearKeptRegisterReading(t); } catch (err) {
        console.warn("[clearUserData] clearKeptRegisterReading threw:", err);
    }
    // Reap the short-lived tailored-canvases store key: tailored canvases
    // now live only in the scout chat's transcript (materialized on click),
    // so nothing writes or reads this — but builds that auto-mounted them
    // left the blob behind.
    safeRemove(localStorage, `origin-tailored-canvases:${t}`);
    safeRemove(localStorage, "chatAutoOpened");
}

// Workflow bucket: every workflow definition + every run for this
// tenant. The IDB store is named `origin-workflows`, separate from
// the chat DB.
export async function clearWorkflowData(tenantId) {
    const t = tk(tenantId);
    try { await deleteAllWorkflowsForTenant(t); } catch (err) {
        console.warn("[clearUserData] deleteAllWorkflowsForTenant threw:", err);
    }
}

// The dashboard no longer connects chat tools, but a browser that once
// did still holds their OAuth tokens and API keys. Every tenant's and
// user's records go, since no code reads them any more.
export function discardChatToolConnections() {
    try {
        const keys = [];
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k?.startsWith("mcp:") && k.endsWith(":conn")) keys.push(k);
        }
        for (const k of keys) localStorage.removeItem(k);
    } catch { /* storage unavailable */ }
    safeRemove(sessionStorage, "mcp:pkce-state");
    safeRemove(sessionStorage, "mcp:pkce");
}
